import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TourProvider,
  useSidebar,
  useTour,
  type ITourStep,
} from '@kognitos/lattice';

import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import { useGateValue } from '@statsig/react-bindings';
import { FEATURE_FLAGS } from '@/shared/types/features';

import { areToursDisabled } from '../../persistence';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';
import { useTourStore, selectActiveTourId } from '../../tours.store';
import {
  useNudgeStore,
  selectNudgeConditions,
  selectClearNudgeCondition,
} from '../nudges.store';
import { isNudgeDismissed, markNudgeDismissed } from '../persistence';
import { useNudgeAnalytics } from '../hooks/use-nudge-analytics';
import { NUDGE_REGISTRY } from '../registry';
import type { INudgeConfig } from '../types';

const SIDEBAR_WIDTH_EXPANDED_PX = 256; // 16rem
const SIDEBAR_WIDTH_COLLAPSED_PX = 48; // 3rem

/**
 * Selects the highest-priority active nudge that hasn't been dismissed.
 *
 * @param conditions - Current nudge conditions from the store
 * @returns The nudge config to display, or null
 */
function resolveActiveNudge(
  conditions: Partial<Record<string, boolean>>,
): INudgeConfig | null {
  let best: INudgeConfig | null = null;

  for (const config of NUDGE_REGISTRY) {
    if (conditions[config.id] !== true) continue;
    if (isNudgeDismissed(config.id, config.persistence)) continue;
    if (!best || config.priority > best.priority) {
      best = config;
    }
  }

  return best;
}

/**
 * Auto-starts the tour on mount. Must be rendered inside a TourProvider.
 */
function NudgeAutoStart() {
  const { startTour, isActive } = useTour();

  // eslint-disable-next-line no-restricted-syntax -- reason: Auto-start the tour once when the nudge mounts
  useEffect(() => {
    if (!isActive) {
      startTour();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

/**
 * Renders a single active nudge using the lattice TourProvider
 * with a targetless step. A viewport-fixed overlay div that spans
 * only the content area (to the right of the sidebar) is used as the
 * container so the card stays visible and never overlaps the sidebar.
 */
function ActiveNudge({ nudge }: { nudge: INudgeConfig }) {
  const { t } = useTranslation(nudge.namespace ?? 'tours');
  const navigate = useNavigate();
  const { open: sidebarOpen } = useSidebar();
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const clearCondition = useNudgeStore(selectClearNudgeCondition);
  const { trackDismissed, trackActionClicked, trackShown } =
    useNudgeAnalytics(nudge);
  const containerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line no-restricted-syntax -- reason: Track nudge shown event once per nudge after committed render
  useEffect(() => {
    trackShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudge.id]);

  const dismiss = () => {
    markNudgeDismissed(nudge.id, nudge.persistence);
    clearCondition(nudge.id);
  };

  const handleDismiss = () => {
    dismiss();
    trackDismissed();
  };

  const handleAction = () => {
    trackActionClicked();
    if (nudge.onAction) {
      nudge.onAction({ dismiss, navigate, orgId, workspaceId });
    } else {
      dismiss();
    }
  };

  const sidebarWidth = sidebarOpen
    ? SIDEBAR_WIDTH_EXPANDED_PX
    : SIDEBAR_WIDTH_COLLAPSED_PX;

  const step: ITourStep = {
    title: t(nudge.titleKey),
    description: t(nudge.descriptionKey),
    fixedPosition: nudge.position ?? 'bottom-left',
    previousLabel: t(nudge.dismissLabelKey ?? 'common.action.dismiss'),
    finishLabel: t(nudge.actionLabelKey),
  };

  return (
    <>
      {/*
       * Viewport-fixed positioning boundary for the nudge card.
       * TourProvider's `container` prop portals the card here and switches
       * from `fixed` to `absolute` positioning relative to this element.
       * By offsetting `left` by the current sidebar width we guarantee
       * the card (e.g. "bottom-left") stays within the content area and
       * never overlaps the sidebar. Using a fixed-height (viewport) div
       * instead of SidebarInset avoids the card being pushed below the
       * fold when the page content is taller than the viewport.
       */}
      <div
        ref={containerRef}
        className="pointer-events-none fixed inset-0 [&>*]:pointer-events-auto"
        style={{ left: sidebarWidth }}
      />
      <TourProvider
        steps={[step]}
        variant="default"
        size="sm"
        showOverlay={false}
        onComplete={handleAction}
        onSkip={handleDismiss}
        alwaysShowPrevious
        previousButtonVariant="ghost"
        nextButtonVariant="secondary"
        container={containerRef}
      >
        <NudgeAutoStart />
      </TourProvider>
    </>
  );
}

/**
 * Global component that evaluates registered nudge conditions and renders
 * the highest-priority active nudge as a sticky card.
 *
 * Uses the lattice `TourProvider` with `showOverlay={false}` and a single
 * targetless step. An internal viewport-fixed container offset by the
 * sidebar width ensures the card stays in the content area and never
 * overlaps the sidebar.
 *
 * Automatically hides when any managed tour is active.
 *
 * Place once inside a `SidebarProvider` in the main app layout.
 *
 * @returns {JSX.Element | null} The active nudge or null
 *
 * @example
 * ```tsx
 * <SidebarProvider>
 *   <AppSidebar />
 *   <SidebarInset>...</SidebarInset>
 *   <NudgeManager />
 * </SidebarProvider>
 * ```
 */
export function NudgeManager() {
  const isFeatureEnabled = useGateValue(FEATURE_FLAGS.ENABLE_ONBOARDING_TOURS);
  const activeTourId = useTourStore(selectActiveTourId);
  const conditions = useNudgeStore(selectNudgeConditions);
  const activeNudge = resolveActiveNudge(conditions);

  if (
    !isFeatureEnabled ||
    areToursDisabled() ||
    activeTourId ||
    !activeNudge ||
    isE2ETestMode()
  )
    return null;

  return <ActiveNudge key={activeNudge.id} nudge={activeNudge} />;
}
